import Close from "@mui/icons-material/Close";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { Prisma } from "encamp-prisma/generated/browser";
import { assertNever } from "encamp-shared/src/utils/assertNever";
import { InvoiceAuditLineFragment } from "generated-graphql/graphql";
import { useId, useState } from "react";

import { formatCalculationMoney, formatUnitRate } from "./formatAuditValues";

type Calculation = NonNullable<InvoiceAuditLineFragment["calculation"]>;

function calculationTitle(calculation: Calculation): string {
  switch (calculation.rule) {
    case "PER_UNIT":
      return "Per unit calculation";
    case "MINIMUM":
      return calculation.quantityCharge != null &&
        calculation.minimumAmount != null &&
        new Prisma.Decimal(calculation.quantityCharge).lt(calculation.minimumAmount)
        ? "Minimum charge applied"
        : "Minimum charge calculation";
    case "BASE_PLUS_ADDITIONAL":
      return "Base charge + additional units";
    case "FLAT":
      return "Contract flat fee";
    case "CASE_BY_CASE":
    case "QUOTE":
    case "DOCUMENTED_COST":
      return "Expected charge calculation";
    default:
      return assertNever(calculation.rule);
  }
}

export function InvoiceAuditCalculationPopover({
  calculation,
  unit,
}: {
  calculation: Calculation;
  unit: string | null | undefined;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const id = useId();
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);
  const title = calculationTitle(calculation);
  const quantity = (value: string | null | undefined) =>
    value == null ? "—" : `${value} ${unit ?? ""}`.trim();
  const unitRate = (value: string | null | undefined) => formatUnitRate(value, unit);
  const hasFormula = (
    {
      PER_UNIT: true,
      MINIMUM: true,
      BASE_PLUS_ADDITIONAL: true,
      FLAT: true,
      CASE_BY_CASE: false,
      QUOTE: false,
      DOCUMENTED_COST: false,
    } satisfies Record<Calculation["rule"], boolean>
  )[calculation.rule];

  return (
    <>
      <IconButton
        size="small"
        color="primary"
        aria-label="How the expected charge was calculated"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ p: 0.5, flexShrink: 0 }}
      >
        <InfoOutlined fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            id,
            role: "dialog",
            "aria-labelledby": `${id}-title`,
            sx: { width: 400, maxWidth: "calc(100vw - 32px)" },
          },
        }}
      >
        <Stack spacing={1} sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography id={`${id}-title`} variant="subtitle2" component="h3">
              {title}
            </Typography>
            <IconButton size="small" aria-label="Close calculation" onClick={close} autoFocus>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
          {hasFormula ? (
            <Table
              size="small"
              aria-label="Expected charge calculation"
              sx={{
                fontVariantNumeric: "tabular-nums",
                "& .MuiTableCell-root": {
                  border: 0,
                  px: 0,
                  py: 0.75,
                  overflowWrap: "anywhere",
                },
                "& .MuiTableCell-body:last-child": {
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  width: "1%",
                  pl: 2,
                },
              }}
            >
              <TableBody>
                {calculation.rule === "BASE_PLUS_ADDITIONAL" ? (
                  <>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Base charge · includes {quantity(calculation.includedQuantity)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCalculationMoney(calculation.baseCharge)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Additional · {quantity(calculation.additionalQuantity)} ×{" "}
                        {unitRate(calculation.additionalUnitRate)}
                      </TableCell>
                      <TableCell align="right">
                        + {formatCalculationMoney(calculation.additionalCharge)}
                      </TableCell>
                    </TableRow>
                  </>
                ) : calculation.rule === "FLAT" ? (
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Contract flat amount
                    </TableCell>
                    <TableCell align="right">
                      {formatCalculationMoney(calculation.amount)}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        {quantity(calculation.quantity)} × {unitRate(calculation.unitRate)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCalculationMoney(
                          calculation.rule === "MINIMUM"
                            ? calculation.quantityCharge
                            : calculation.quantity != null && calculation.unitRate != null
                              ? new Prisma.Decimal(calculation.quantity)
                                  .mul(calculation.unitRate)
                                  .toFixed()
                              : null
                        )}
                      </TableCell>
                    </TableRow>
                    {calculation.rule === "MINIMUM" && (
                      <TableRow>
                        <TableCell component="th" scope="row">
                          Contract minimum
                        </TableCell>
                        <TableCell align="right">
                          {formatCalculationMoney(calculation.minimumAmount)}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2">{calculation.explanation}</Typography>
          )}
          {calculation.rule === "MINIMUM" && (
            <Typography variant="body2" color="text.secondary">
              The higher amount applies.
            </Typography>
          )}
        </Stack>
      </Popover>
    </>
  );
}
